import {
  customerIdentifierStatusValues,
  customerIdentifierTypeValues,
  customerStatusValues,
} from '../customers/customers.dto.js';
import { employeeStatusValues, employmentTypeValues } from '../employees/employees.dto.js';
import { z } from 'zod';

import {
  customerIdentifierSyncEventTypeValues,
  customerSyncEventTypeValues,
  type EmployeeAssignmentSyncPayload,
  type EmployeeSyncEventPayload,
  type EmployeeSyncPayload,
  type CustomerIdentifierSyncEventPayload,
  type CustomerIdentifierSyncPayload,
  type CustomerSyncEventPayload,
  type CustomerSyncPayload,
  type PurchaseReassignmentAuditSyncEventPayload,
  type PurchaseReassignmentAuditSyncPayload,
  type SyncAckRequest,
  type SyncAckResponse,
  type SyncEvent,
  type SyncEventResult,
  type SyncPullItem,
  type SyncPullRequest,
  type SyncPullResponse,
  type SyncPushRequest,
  type SyncPushResponse,
  employeeSyncEventTypeValues,
  purchaseReassignmentAuditSyncEventTypeValues,
} from './sync.dto.js';
import { HoldingUnitSyncEventSchema } from './holding-units-sync.schema.js';
import { SyncClientIdentitySchema, SyncPullEntityTypeSchema } from './sync-common.schema.js';
import {
  isValidSyncDeliveryErrorDetails,
  SyncDeliveryErrorDetailsSchema,
  SyncDeliveryErrorCodeSchema,
} from './sync-delivery-errors.js';
import {
  CoverShipmentDepartedEventPayloadSchema,
  CoverShipmentEvidenceUpdatedEventPayloadSchema,
  CoverShipmentSavedEventPayloadSchema,
  CoverShipmentVoidedEventPayloadSchema,
} from './cover-shipments/cover-shipments-sync.schema.js';
import {
  InboundReceiptCanceledEventPayloadSchema,
  InboundReceiptComparisonReviewedEventPayloadSchema,
  InboundReceiptConfirmedEventPayloadSchema,
  InboundReceiptInventoryPostedEventPayloadSchema,
  InboundReceiptInventoryPostingReversedEventPayloadSchema,
  InboundReceiptOfficialStateReplacedEventPayloadSchema,
} from './inbound-receipts/inbound-receipts-sync.schema.js';
import { IncomingTransferUpsertedEventPayloadSchema } from './incoming-transfers/incoming-transfers-sync.schema.js';
import {
  PurchaseCanceledEventPayloadSchema,
  PurchaseCommittedEventPayloadSchema,
  PurchaseLimitLedgerEntryRecordedEventPayloadSchema,
  PurchaseNoteAddedEventPayloadSchema,
  PurchasePrintStatusUpdatedEventPayloadSchema,
} from './purchases/purchases-sync.schema.js';
import {
  ShipmentDraftEventPayloadSchema,
  ShipmentDriverAssignmentSyncPayloadSchema,
  ShipmentLogisticsShellReversedEventPayloadSchema,
  ShipmentLogisticsShellUpsertedEventPayloadSchema,
  ShipmentSavedEventPayloadSchema,
  ShipmentScaleTicketEventPayloadSchema,
  ShipmentWeighEventSyncPayloadSchema,
  ShipmentVoidedEventPayloadSchema,
} from './shipments/shipments-sync.schema.js';
import { SourcePreparationRequestStatusUpdatedEventPayloadSchema } from './source-preparation/source-preparation-sync.schema.js';

const timestampSchema = z.iso.datetime();
const dateSchema = z.iso.date();
const employeeNumberSchema = z.string().trim().min(1).max(50);
const timeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .refine(isValidIanaTimeZone, { message: 'Invalid time zone' });

function isValidIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export const EmployeeAssignmentSyncPayloadSchema: z.ZodType<EmployeeAssignmentSyncPayload> =
  z.object({
    companyId: z.uuid(),
    employeeId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid().nullable(),
    isPrimary: z.boolean(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const EmployeeSyncPayloadSchema: z.ZodType<EmployeeSyncPayload> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid().nullable(),
  userId: z.uuid().nullable(),
  employeeNumber: employeeNumberSchema.nullable(),
  timezone: timeZoneSchema.nullable(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  displayName: z.string().min(1),
  status: z.enum(employeeStatusValues),
  hireDate: dateSchema.nullable(),
  terminationDate: dateSchema.nullable(),
  employmentType: z.enum(employmentTypeValues).nullable(),
  assignments: z.array(EmployeeAssignmentSyncPayloadSchema).default([]),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const EmployeeSyncEventPayloadSchema: z.ZodType<EmployeeSyncEventPayload> = z.object({
  employee: EmployeeSyncPayloadSchema,
});

export const CustomerSyncPayloadSchema: z.ZodType<CustomerSyncPayload> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  displayName: z.string().trim().min(1),
  status: z.enum(customerStatusValues),
  isPublic: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const CustomerSyncEventPayloadSchema: z.ZodType<CustomerSyncEventPayload> = z.object({
  customer: CustomerSyncPayloadSchema,
});

export const CustomerIdentifierSyncPayloadSchema: z.ZodType<CustomerIdentifierSyncPayload> =
  z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    customerId: z.uuid(),
    type: z.enum(customerIdentifierTypeValues),
    valueHash: z.string().trim().min(1),
    valueMasked: z.string().trim().min(1),
    status: z.enum(customerIdentifierStatusValues),
    isDefault: z.boolean(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const CustomerIdentifierSyncEventPayloadSchema: z.ZodType<CustomerIdentifierSyncEventPayload> =
  z.object({
    customerIdentifier: CustomerIdentifierSyncPayloadSchema,
  });

export const PurchaseReassignmentAuditSyncPayloadSchema: z.ZodType<PurchaseReassignmentAuditSyncPayload> =
  z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    actorUserId: z.uuid(),
    action: z.string().trim().min(1),
    purchaseId: z.uuid().nullable(),
    receiptNumber: z.string().trim().min(1),
    occurredAt: timestampSchema,
    createdAt: timestampSchema,
  });

export const PurchaseReassignmentAuditSyncEventPayloadSchema: z.ZodType<PurchaseReassignmentAuditSyncEventPayload> =
  z.object({
    purchaseReassignmentAudit: PurchaseReassignmentAuditSyncPayloadSchema,
  });

const SyncEventBaseSchema = {
  eventId: z.uuid(),
  entityId: z.uuid(),
  occurredAt: timestampSchema,
  source: z.string().trim().min(1).nullable().optional(),
};

const EmployeeSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('employee'),
  eventType: z.enum(employeeSyncEventTypeValues),
  payload: EmployeeSyncEventPayloadSchema,
});

const CustomerSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('customer'),
  eventType: z.enum(customerSyncEventTypeValues),
  payload: CustomerSyncEventPayloadSchema,
});

const CustomerIdentifierSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('customerIdentifier'),
  eventType: z.enum(customerIdentifierSyncEventTypeValues),
  payload: CustomerIdentifierSyncEventPayloadSchema,
});

const PurchaseReassignmentAuditSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('purchaseReassignmentAudit'),
  eventType: z.enum(purchaseReassignmentAuditSyncEventTypeValues),
  payload: PurchaseReassignmentAuditSyncEventPayloadSchema,
});

const PurchaseCommittedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('purchase'),
  eventType: z.literal('purchaseCommitted'),
  payload: PurchaseCommittedEventPayloadSchema,
});

const PurchaseCanceledSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('purchase'),
  eventType: z.literal('purchaseCanceled'),
  payload: PurchaseCanceledEventPayloadSchema,
});

const PurchasePrintStatusUpdatedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('purchase'),
  eventType: z.literal('purchasePrintStatusUpdated'),
  payload: PurchasePrintStatusUpdatedEventPayloadSchema,
});

const PurchaseNoteAddedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('purchase'),
  eventType: z.literal('purchaseNoteAdded'),
  payload: PurchaseNoteAddedEventPayloadSchema,
});

const PurchaseLimitLedgerEntryRecordedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('purchaseLimitLedgerEntry'),
  eventType: z.literal('purchaseLimitLedgerEntryRecorded'),
  payload: PurchaseLimitLedgerEntryRecordedEventPayloadSchema,
});

const ShipmentDraftCreatedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentCreated'),
  payload: ShipmentDraftEventPayloadSchema,
});

const ShipmentDraftUpdatedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentUpdated'),
  payload: ShipmentDraftEventPayloadSchema,
});

const ShipmentSavedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentSaved'),
  payload: ShipmentSavedEventPayloadSchema,
});

const ShipmentVoidedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentVoided'),
  payload: ShipmentVoidedEventPayloadSchema,
});

const ShipmentScaleTicketAddedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentScaleTicketAdded'),
  payload: ShipmentScaleTicketEventPayloadSchema,
});

const ShipmentScaleTicketUpdatedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentScaleTicketUpdated'),
  payload: ShipmentScaleTicketEventPayloadSchema,
});

const ShipmentScaleTicketVoidedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentScaleTicketVoided'),
  payload: ShipmentScaleTicketEventPayloadSchema,
});

const ShipmentWeighEventAddedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentWeighEventAdded'),
  payload: ShipmentWeighEventSyncPayloadSchema,
});

const ShipmentWeighEventUpdatedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentWeighEventUpdated'),
  payload: ShipmentWeighEventSyncPayloadSchema,
});

const ShipmentWeighEventVoidedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentWeighEventVoided'),
  payload: ShipmentWeighEventSyncPayloadSchema,
});

const ShipmentDriverAssignmentUpdatedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipment'),
  eventType: z.literal('shipmentDriverAssignmentUpdated'),
  payload: ShipmentDriverAssignmentSyncPayloadSchema,
});

const ShipmentLogisticsShellUpsertedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipmentLogisticsShell'),
  eventType: z.literal('shipmentLogisticsShellUpserted'),
  payload: ShipmentLogisticsShellUpsertedEventPayloadSchema,
});

const ShipmentLogisticsShellReversedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('shipmentLogisticsShell'),
  eventType: z.literal('shipmentLogisticsShellReversed'),
  payload: ShipmentLogisticsShellReversedEventPayloadSchema,
});

const SourcePreparationRequestStatusUpdatedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('sourcePreparationRequest'),
  eventType: z.literal('sourcePreparationRequestStatusUpdated'),
  payload: SourcePreparationRequestStatusUpdatedEventPayloadSchema,
});

const InboundReceiptConfirmedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('inboundReceipt'),
  eventType: z.literal('inboundReceiptConfirmedV2'),
  payload: InboundReceiptConfirmedEventPayloadSchema,
});

const InboundReceiptCanceledSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('inboundReceipt'),
  eventType: z.literal('inboundReceiptCanceledV2'),
  payload: InboundReceiptCanceledEventPayloadSchema,
});

const InboundReceiptOfficialStateReplacedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('inboundReceipt'),
  eventType: z.literal('inboundReceiptOfficialStateReplacedV2'),
  payload: InboundReceiptOfficialStateReplacedEventPayloadSchema,
});

const InboundReceiptComparisonReviewedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('inboundReceipt'),
  eventType: z.literal('inboundReceiptComparisonReviewedV2'),
  payload: InboundReceiptComparisonReviewedEventPayloadSchema,
});

const InboundReceiptInventoryPostedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('inboundReceipt'),
  eventType: z.literal('inboundReceiptInventoryPostedV2'),
  payload: InboundReceiptInventoryPostedEventPayloadSchema,
});

const InboundReceiptInventoryPostingReversedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('inboundReceipt'),
  eventType: z.literal('inboundReceiptInventoryPostingReversedV2'),
  payload: InboundReceiptInventoryPostingReversedEventPayloadSchema,
});

const IncomingTransferUpsertedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('incomingTransfer'),
  eventType: z.literal('incomingTransferUpserted'),
  payload: IncomingTransferUpsertedEventPayloadSchema,
});

const CoverShipmentSavedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('coverShipment'),
  eventType: z.literal('coverShipmentSaved'),
  payload: CoverShipmentSavedEventPayloadSchema,
});

const CoverShipmentVoidedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('coverShipment'),
  eventType: z.literal('coverShipmentVoided'),
  payload: CoverShipmentVoidedEventPayloadSchema,
});

const CoverShipmentEvidenceUpdatedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('coverShipment'),
  eventType: z.literal('coverShipmentEvidenceUpdated'),
  payload: CoverShipmentEvidenceUpdatedEventPayloadSchema,
});

const CoverShipmentDepartedSyncEventSchema = z.object({
  ...SyncEventBaseSchema,
  entityType: z.literal('coverShipment'),
  eventType: z.literal('coverShipmentDeparted'),
  payload: CoverShipmentDepartedEventPayloadSchema,
});

const syncPullItemSchemas = [
  EmployeeSyncEventSchema,
  CustomerSyncEventSchema,
  CustomerIdentifierSyncEventSchema,
  PurchaseReassignmentAuditSyncEventSchema,
  PurchaseCommittedSyncEventSchema,
  PurchaseCanceledSyncEventSchema,
  PurchasePrintStatusUpdatedSyncEventSchema,
  PurchaseNoteAddedSyncEventSchema,
  PurchaseLimitLedgerEntryRecordedSyncEventSchema,
  ShipmentDraftCreatedSyncEventSchema,
  ShipmentDraftUpdatedSyncEventSchema,
  ShipmentSavedSyncEventSchema,
  ShipmentVoidedSyncEventSchema,
  ShipmentScaleTicketAddedSyncEventSchema,
  ShipmentScaleTicketUpdatedSyncEventSchema,
  ShipmentScaleTicketVoidedSyncEventSchema,
  ShipmentWeighEventAddedSyncEventSchema,
  ShipmentWeighEventUpdatedSyncEventSchema,
  ShipmentWeighEventVoidedSyncEventSchema,
  ShipmentDriverAssignmentUpdatedSyncEventSchema,
  ShipmentLogisticsShellUpsertedSyncEventSchema,
  ShipmentLogisticsShellReversedSyncEventSchema,
  SourcePreparationRequestStatusUpdatedSyncEventSchema,
  InboundReceiptConfirmedSyncEventSchema,
  InboundReceiptCanceledSyncEventSchema,
  InboundReceiptOfficialStateReplacedSyncEventSchema,
  InboundReceiptComparisonReviewedSyncEventSchema,
  InboundReceiptInventoryPostedSyncEventSchema,
  InboundReceiptInventoryPostingReversedSyncEventSchema,
  IncomingTransferUpsertedSyncEventSchema,
  CoverShipmentSavedSyncEventSchema,
  CoverShipmentVoidedSyncEventSchema,
  CoverShipmentEvidenceUpdatedSyncEventSchema,
  CoverShipmentDepartedSyncEventSchema,
] as const;

export const SyncPullItemSchema: z.ZodType<SyncPullItem> = z.union(syncPullItemSchemas);

export const SyncEventSchema: z.ZodType<SyncEvent> = z.union([
  ...syncPullItemSchemas,
  HoldingUnitSyncEventSchema,
]);

export const SyncPushEventSchema = SyncEventSchema;

export const SyncPushRequestSchema: z.ZodType<SyncPushRequest> = z.object({
  client: SyncClientIdentitySchema,
  events: z.array(SyncPushEventSchema).nonempty(),
});

const SyncEventSuccessResultSchema = z
  .object({
    eventId: z.uuid(),
    status: z.literal('ok'),
    cloudApplyStatus: z.enum(['projected', 'ingested_projection_pending']).optional(),
  })
  .strict();

const SyncEventErrorResultSchema = z
  .object({
    eventId: z.uuid(),
    status: z.literal('error'),
    errorCode: SyncDeliveryErrorCodeSchema,
    message: z.string().trim().min(1).max(1024).optional(),
    details: SyncDeliveryErrorDetailsSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (!isValidSyncDeliveryErrorDetails(value.errorCode, value.details)) {
      context.addIssue({
        code: 'custom',
        message: `Invalid details for sync delivery error ${value.errorCode}`,
        path: ['details'],
      });
    }
  });

export const SyncEventResultSchema: z.ZodType<SyncEventResult> = z.discriminatedUnion('status', [
  SyncEventSuccessResultSchema,
  SyncEventErrorResultSchema,
]);

export const SyncPushResponseSchema: z.ZodType<SyncPushResponse> = z.object({
  results: z.array(SyncEventResultSchema),
});

export const SyncPullRequestSchema: z.ZodType<SyncPullRequest> = z.object({
  client: SyncClientIdentitySchema,
  cursor: z.string().trim().min(1).nullable().optional(),
  entityTypes: z.array(SyncPullEntityTypeSchema).nonempty().optional(),
});

export const SyncPullResponseSchema: z.ZodType<SyncPullResponse> = z.object({
  items: z.array(SyncPullItemSchema),
  nextCursor: z.string().trim().min(1).nullable(),
});

export const SyncAckRequestSchema: z.ZodType<SyncAckRequest> = z
  .object({
    client: SyncClientIdentitySchema,
    cursor: z.string().trim().min(1).nullable().optional(),
    eventIds: z.array(z.uuid()).nonempty().optional(),
  })
  .refine((value) => Boolean(value.cursor) || Boolean(value.eventIds?.length), {
    message: 'cursor or eventIds is required',
    path: ['cursor'],
  });

export const SyncAckResponseSchema: z.ZodType<SyncAckResponse> = z.object({
  accepted: z.number().int().min(0),
  rejected: z.number().int().min(0),
});
